export const GENERIC_ERROR_MESSAGE = 'Ocurrió un error inesperado. Inténtalo nuevamente.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Extracts an HTTP-like status from Supabase/PostgREST/fetch style errors. */
export function getErrorStatus(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;
  const status = error.status ?? error.statusCode;
  if (typeof status === 'number') return status;
  if (typeof status === 'string' && /^\d{3}$/.test(status)) return Number(status);
  return undefined;
}

/** Postgres SQLSTATE of a PostgREST error, if any. */
export function getErrorCode(error: unknown): string | undefined {
  return isRecord(error) && typeof error.code === 'string' ? error.code : undefined;
}

// Raw database messages are English and may expose schema details; show these instead.
const DATABASE_MESSAGES: Record<string, string> = {
  '23505': 'Ya existe un registro con esos datos.',
  '23503': 'La operación hace referencia a un registro que no existe o que está en uso.',
  '23514': 'Algún dato no cumple las reglas de validación.',
  '23502': 'Falta un dato obligatorio.',
  '22001': 'Algún texto supera la longitud permitida.',
  '22P02': 'Algún dato tiene un formato inválido.',
  '42501': 'No tienes permisos para realizar esta operación.',
  PGRST116: 'El registro no existe o no tienes acceso a él.',
};

function isEnglishDatabaseMessage(message: string): boolean {
  return /permission denied|row-level security|violates|duplicate key|invalid input|null value/i.test(
    message,
  );
}

/**
 * Normalizes any thrown value into a user-facing Spanish message.
 * Messages raised by our own SQL guards (Spanish) are preserved; generic
 * database errors are translated by SQLSTATE.
 */
export function getErrorMessage(error: unknown, fallback = GENERIC_ERROR_MESSAGE): string {
  if (typeof error === 'string' && error.trim()) return error;

  const code = getErrorCode(error);
  const message = isRecord(error) && typeof error.message === 'string' ? error.message.trim() : '';

  if (code && code in DATABASE_MESSAGES) {
    return message && !isEnglishDatabaseMessage(message) && code === '42501'
      ? message
      : (DATABASE_MESSAGES[code] ?? fallback);
  }
  if (message && isEnglishDatabaseMessage(message)) return fallback;
  if (message) return message;
  return fallback;
}
